import { links } from "@/components/ads/config/burgerMenu";
import { logout } from "@/common/services/Auth";
import { useDeviceType } from "@/common/hooks";
import { deviceTypeEnum } from "@/common/utils/device";
import { useAppStore } from "@/common/store/appStore";
import { useCallback, useMemo, useRef, useState } from "react";
import { useUserStore } from "@/common/store/userStore";
import { routes } from "@/common/utils/routes";

import BlogIcon from "../images/icons/blog.svg?react";
import DownloadAppIcon from "../images/icons/download-app.svg?react";
import AiIcon from "../images/icons/rg-ai.svg?react";
import CamsIcon from "../images/icons/cam.svg?react";
import HelpIcon from "../images/icons/help.svg?react";
import SettingsIcon from "../images/icons/settings.svg?react";
import SettingsCookieIcon from "../images/icons/settings-cookie.svg?react";
import LogoutIcon from "../images/icons/logout.svg?react";
import GuidelinesIcon from "../images/icons/guidelines.svg?react";
import ExternalLinkIcon from "../images/icons/external-link.svg?react";
import ExperimentsIcon from "@/common/images/icons/plus.svg?react";

export const useBurgerMenu = () => {
  const [activeMenuItem, setActiveMenuItem] = useState<string>("");
  const toggleBurgerMenu = useAppStore((state) => state.toggleBurgerMenu);
  const isBurgerMenuOpen = useAppStore((state) => state.isBurgerMenuOpen);
  const user = useUserStore((state) => state.currentUser);
  const [devMode, setDevMode] = useState<boolean>(false);

  const isDesktop = useDeviceType() === deviceTypeEnum.DESKTOP;
  const devClickCounter = useRef(0);

  const handleCloseMenu = useCallback(() => {
    toggleBurgerMenu();
  }, [toggleBurgerMenu]);

  const onSetActiveMenuItem = useCallback((item: string) => {
    setActiveMenuItem(item);
  }, []);

  const handleDevModeClick = useCallback(() => {
    if (devMode) {
      return;
    }

    const waitTime = 2000;
    const clickThreshold = 10;

    if (devClickCounter.current === 0) {
      setTimeout(() => {
        devClickCounter.current = 0;
      }, waitTime);
    }

    devClickCounter.current++;

    if (devClickCounter.current >= clickThreshold) {
      setDevMode(true);
    }
  }, []);

  const menuItems = useMemo(() => ([
    {
      icon: CamsIcon,
      title: "RedGIFs Cams",
      rightIcon: ExternalLinkIcon,
      onClick: () => {
        window.location.href = isDesktop ? links.RGCamsDesktop : links.RGCams;
      },
    },
    {
      icon: AiIcon,
      title: "RedGIFs.ai",
      rightIcon: ExternalLinkIcon,
      target: "_blank",
      rel: "noopener",
      external: true,
      link: isDesktop ? links.RGAiDesktop : links.RGAiMobile,
    },
    {
      icon: DownloadAppIcon,
      title: "RedGIFs App",
      link: routes.app(),
    },
    {
      icon: BlogIcon,
      title: "Creator's Blog",
      link: "https://redgifs-creators.com/",
      target: "_blank",
      rel: "noopener",
      external: true,
      delimiter: true,
      rightIcon: ExternalLinkIcon,
    },
    ...(user ? [
      {
        icon: SettingsIcon,
        title: "Settings",
        link: routes.accountSettings(),
      },
    ] : []),
    {
      icon: HelpIcon,
      title: "FAQ",
      link: routes.faq(),
      delimiter: true,
    },
    {
      title: "Terms of Service",
      link: "https://help.redgifs.com/hc/en-us/articles/39353208672785-RedGIFS-Terms-of-Service",
      external: true,
      target: "_blank",
      rel: "noopener",
      rightIcon: ExternalLinkIcon,
      icon: GuidelinesIcon
    },
    {
      icon: GuidelinesIcon,
      title: "Trust & Safety",
      subNav: [
        {
          title: "Acceptable Use Policy",
          link: "https://help.redgifs.com/hc/en-us/articles/39353658178833-Acceptable-Use-Policy",
          external: true,
          target: "_blank",
          rel: "noopener",
          rightIcon: ExternalLinkIcon,
          icon: GuidelinesIcon
        },
        {
          title: "Age Verification Policy",
          link: "https://help.redgifs.com/hc/en-us/articles/39353731496209-Age-Verification-Policy",
          external: true,
          target: "_blank",
          rel: "noopener",
          rightIcon: ExternalLinkIcon,
          icon: GuidelinesIcon
        },
        {
          title: "Complaints Policy",
          link: "https://help.redgifs.com/hc/en-us/articles/39353814880657-Complaints-Policy",
          external: true,
          target: "_blank",
          rel: "noopener",
          rightIcon: ExternalLinkIcon,
          icon: GuidelinesIcon
        },
        {
          title: "Appeals Policy",
          link: "https://help.redgifs.com/hc/en-us/articles/39353753351953-Appeals-Policy",
          external: true,
          target: "_blank",
          rel: "noopener",
          rightIcon: ExternalLinkIcon,
          icon: GuidelinesIcon
        },
        {
          title: "Legal Requests Policy",
          link: "https://help.redgifs.com/hc/en-us/articles/39353825911441-Law-Enforcement-Request-Policy",
          external: true,
          target: "_blank",
          rel: "noopener",
          rightIcon: ExternalLinkIcon,
          icon: GuidelinesIcon
        },
        {
          title: "Anti-Human Trafficking, Anti-Sex Trafficking, and Anti-Prostitution Policy",
          link: "https://help.redgifs.com/hc/en-us/articles/39353744989201-Anti-Human-Trafficking-Anti-Sex-Trafficking-and-Anti-Prostitution-Policy",
          external: true,
          target: "_blank",
          rel: "noopener",
          rightIcon: ExternalLinkIcon,
          icon: GuidelinesIcon
        },
      ]
    },
    {
      icon: GuidelinesIcon,
      title: "Privacy Policy",
      subNav: [
        {
          title: "Cookie Use Policy",
          link: "https://help.redgifs.com/hc/en-us/articles/39354442324753-RedGIFs-Privacy-Policy#h_01K5XSGRYKKA7PS79VQ30VV20E",
          external: true,
          target: "_blank",
          rel: "noopener",
          rightIcon: ExternalLinkIcon,
          icon: GuidelinesIcon
        },
        {
          title: "Privacy Policy ",
          link: "https://help.redgifs.com/hc/en-us/articles/39354442324753-RedGIFs-Privacy-Policy",
          external: true,
          target: "_blank",
          rel: "noopener",
          rightIcon: ExternalLinkIcon,
          icon: GuidelinesIcon
        },
        {
          title: "California Privacy Policy",
          link: "https://help.redgifs.com/hc/en-us/articles/39354442324753-RedGIFs-Privacy-Policy#h_01K5XSGRZ0Q41ZKR223EPF8BED",
          external: true,
          target: "_blank",
          rel: "noopener",
          rightIcon: ExternalLinkIcon,
          icon: GuidelinesIcon
        },
      ]
    },
    {
      title: "DMCA",
      link: "https://help.redgifs.com/hc/en-us/articles/39354884961041-DMCA-Policy",
      external: true,
      target: "_blank",
      rel: "noopener",
      rightIcon: ExternalLinkIcon,
      icon: GuidelinesIcon,
    },
    {
      title: "2257",
      link: "https://help.redgifs.com/hc/en-us/articles/39354987548689-18-U-S-C-2257-2257A-Record-Keeping-Compliance-Statement",
      external: true,
      target: "_blank",
      rel: "noopener",
      rightIcon: ExternalLinkIcon,
      icon: GuidelinesIcon,
    },
    {
      title: "Take it Down Act",
      link: "https://help.redgifs.com/hc/en-us/articles/39355007174289-U-S-TAKE-IT-DOWN-Act-Compliance-Policy",
      external: true,
      target: "_blank",
      rel: "noopener",
      rightIcon: ExternalLinkIcon,
      icon: GuidelinesIcon,
    },
    {
      title: "Recommender Guidelines",
      link: "https://help.redgifs.com/hc/en-us/articles/39355219651473-Recommender-System-Guidelines",
      external: true,
      target: "_blank",
      rel: "noopener",
      rightIcon: ExternalLinkIcon,
      delimiter: true,
      icon: GuidelinesIcon,
    },
    {
      icon: SettingsCookieIcon,
      title: "Customise Cookie Preferences",
      className: "cky-banner-element",
    },
    ...(user ? [
      {
        icon: LogoutIcon,
        title: "Log Out",
        className: "logoutMenuItem",
        onClick: () => {
          logout();
        }
      }
    ] : []),
    ...(devMode ? [
      {
        icon: ExperimentsIcon,
        title: "Experiments",
        link: routes.experiments(),
        onClick: () => {
          handleCloseMenu();
        }
      },
    ] : [])
  ]), [user, isDesktop, devMode]);

  return {
    onSetActiveMenuItem,
    handleCloseMenu,
    activeMenuItem,
    isBurgerMenuOpen,
    menuItems,
    handleDevModeClick
  };
};
